import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addRequestType, deleteRequestType, getAdminRequestTypes } from '../../../api/requests';
import { errorMessage } from '../../../api/errors';
import type { RequestType } from '../../../api/types';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useAdminData } from '../../../hooks/useAdminData';
import { logout } from '../../../store/admin.slice';
import { logout as logoutRequest } from '../../../api/auth';
import { ADMIN_STORAGE_KEY } from '../../../api/client';

/**
 * Gestione dei tipi di richiesta: le voci mostrate nel menù "Cosa vuoi fare?"
 * del form. L'elenco include anche i tipi nascosti (`type_hidden`).
 */
export function TypeSection() {
    const admin = useAdminData();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [types, setTypes] = useState<RequestType[]>([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);
    /** Tipo in attesa di conferma di eliminazione (null = nessun dialog aperto). */
    const [pendingDelete, setPendingDelete] = useState<RequestType | null>(null);

    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        dispatch(logout());
        // M4: azzera anche il cookie di autenticazione lato server.
        void logoutRequest().catch(() => undefined);
        navigate('/admin/login');
    }, [dispatch, navigate]);

    useEffect(() => {
        if (!admin.logged) return;
        setLoading(true);
        getAdminRequestTypes()
            .then(setTypes)
            .catch((err: unknown) => {
                const status = (err as { response?: { status?: number } }).response?.status;
                if (status === 401) handleUnauthorized();
                else setError(errorMessage(err, 'Impossibile caricare i tipi di richiesta.'));
            })
            .finally(() => setLoading(false));
    }, [admin.logged, reload, handleUnauthorized]);

    const handleAdd = async () => {
        if (!admin.logged || name.trim().length === 0) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        const value = name.trim();
        try {
            await addRequestType(value);
            setName('');
            setSuccess(`"${value}" aggiunto.`);
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
            else setError(errorMessage(err, 'Aggiunta non riuscita. Riprova.'));
        } finally {
            setSaving(false);
        }
    };

    // L'eliminazione chiede conferma: il click sull'icona apre il dialog, la DELETE
    // parte solo dopo il "Elimina".
    const confirmDelete = async () => {
        if (!admin.logged || !pendingDelete) return;
        const type = pendingDelete;
        setError(null);
        setSuccess(null);
        try {
            await deleteRequestType(type.type_id);
            setSuccess(`"${type.type_name}" eliminato.`);
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
            else setError(errorMessage(err, 'Eliminazione non riuscita. Riprova.'));
        } finally {
            setPendingDelete(null);
        }
    };

    const canAdd = name.trim().length > 0 && !saving;

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Paper elevation={0} className="border border-white/10 bg-black/40 p-4 lg:col-span-2">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Tipi di richiesta
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mb-4">
                    Le voci mostrate nel menù &quot;Cosa vuoi fare?&quot; del form
                </Typography>

                {loading ? (
                    <LinearProgress />
                ) : types.length === 0 ? (
                    <EmptyState message="Nessun tipo di richiesta." />
                ) : (
                    <div className="flex flex-col gap-3">
                        {types.map((type) => (
                            <div key={type.type_id} className="flex items-center gap-4 rounded-2xl bg-white/5 p-3">
                                <div className="min-w-0 flex-1">
                                    <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                                        {type.type_name}
                                    </Typography>
                                    {type.type_hidden && (
                                        <Typography variant="body2" color="text.secondary">
                                            Nascosto
                                        </Typography>
                                    )}
                                </div>
                                <IconButton
                                    color="error"
                                    onClick={() => setPendingDelete(type)}
                                    title="Elimina tipo"
                                    aria-label={`Elimina ${type.type_name}`}
                                >
                                    <Close sx={{ fontSize: 24 }} />
                                </IconButton>
                            </div>
                        ))}
                    </div>
                )}
            </Paper>

            <Paper elevation={0} className="h-fit border border-white/10 bg-black/40 p-4">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Nuovo tipo
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mb-4">
                    Aggiungi una voce al menù
                </Typography>
                {error && (
                    <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" className="mb-4" onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                <div className="flex flex-col gap-4">
                    <TextField
                        label="Nome del tipo"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && canAdd && handleAdd()}
                    />
                    <Button variant="contained" fullWidth disabled={!canAdd} onClick={handleAdd}>
                        Aggiungi
                    </Button>
                </div>
            </Paper>

            <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
                <DialogTitle>Eliminare il tipo?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingDelete ? `"${pendingDelete.type_name}" verrà eliminato definitivamente.` : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)}>Annulla</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
